// useMyWeeklyRank — returns the signed-in user's weekly leaderboard position.
//
// Strategy: pull the top N (default 500) weekly_xp rows ordered desc, then
// find the user's index. This keeps the query cheap and avoids a Postgres
// window function / RPC. If the user isn't in the top N (or has 0 weekly_xp)
// we return null so the UI can hide gracefully.
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

interface MyRank {
  rank: number;
  weeklyXp: number;
  totalRanked: number;
}

export function useMyWeeklyRank(poolSize = 500) {
  const { user } = useAuth();
  const [data, setData] = useState<MyRank | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data: rows } = await supabase
        .from("user_xp")
        .select("user_id, weekly_xp")
        .gt("weekly_xp", 0)
        .order("weekly_xp", { ascending: false })
        .limit(poolSize);
      if (cancelled) return;
      if (!rows || rows.length === 0) {
        setData(null);
        setLoading(false);
        return;
      }
      const idx = rows.findIndex((r) => r.user_id === user.id);
      if (idx === -1) {
        setData(null);
      } else {
        setData({
          rank: idx + 1,
          weeklyXp: rows[idx].weekly_xp,
          totalRanked: rows.length,
        });
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, poolSize]);

  return { data, loading };
}
